import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';
import { useState, useRef, useEffect, useMemo } from 'react';
import {
  useGetAssetLocationsQuery,
  useGetAssetPeopleQuery,
  useGetAssetThingsQuery
} from '@ports/internal-services/asset/assetSlice';
import { MultiSelectDropdown } from '../MultiSelectDropdown/MultiSelectDropdown';
import { Button } from '../Button';
import { DateRangePicker } from '../DateRangePicker/DateRangePicker';
import { Select } from '../Select';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterConfig {
  key: string;
  placeholder: string;
  options: FilterOption[];
}

interface FiltersProps {
  setFilterOptions: (options: any) => void;
  filterOptions?: any;
  selectedView?: 'grid' | 'list';
  onViewChange?: (view: 'grid' | 'list') => void;
}

export const Filters = ({
  setFilterOptions,
  filterOptions,
  selectedView = 'grid',
  onViewChange
}: FiltersProps) => {
  const iconClass = 'w-4 h-4 ml-2 !text-[#505050]';
  const { t } = useTranslation();
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);
  const [isFavoritesActive, setIsFavoritesActive] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleFilterChange = (key: string, selectedValues: any) => {
    setFilterOptions((prev: any) => ({
      ...prev,
      [key]: selectedValues
    }));
  };

  const handleFavoritesToggle = () => {
    const newFavoritesState = !isFavoritesActive;
    setIsFavoritesActive(newFavoritesState);

    setFilterOptions((prev: any) => ({
      ...prev,
      favorites: newFavoritesState ? true : undefined
    }));
  };

  const handleViewSelect = (view: 'grid' | 'list') => {
    if (onViewChange) {
      onViewChange(view);
    }
    setIsViewDropdownOpen(false);
  };

  /* ✅ SYNC FAVORITES FROM REDUX */
  useEffect(() => {
    if (typeof filterOptions?.favorites === 'boolean') {
      setIsFavoritesActive(filterOptions.favorites);
    }
  }, [filterOptions?.favorites]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsViewDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: thingsData, refetch: refetchThings } = useGetAssetThingsQuery(
    {}
  );
  const { data: peopleData, refetch: refetchPeople } = useGetAssetPeopleQuery(
    {}
  );
  const { data: locationData, refetch: refetchLocations } =
    useGetAssetLocationsQuery({});

  useEffect(() => {
    refetchThings();
    refetchPeople();
    refetchLocations();
  }, []);

  const thingsOptions = useMemo(() => {
    return (
      thingsData?.data?.map(item => ({
        value: item?.tagName,
        label: item?.tagName
      })) || []
    );
  }, [thingsData]);

  const peopleOptions = useMemo(() => {
    return (
      peopleData?.data?.rows?.map(item => ({
        value: item?.userId,
        label: item?.fullName
      })) || []
    );
  }, [peopleData]);

  const locationOptions = useMemo(() => {
    return (
      locationData?.data?.map((item: string) => ({
        value: item,
        label: item
      })) || []
    );
  }, [locationData]);

  const interactionOptions = [
    {
      value: 'likes',
      label: t('photosPage.liked'),
      startDecorator: <SVGAssets.LikeIcons className={iconClass} />
    },
    {
      value: 'comments',
      label: t('photosPage.comments'),
      startDecorator: <SVGAssets.CommentIcons className={iconClass} />
    },
    {
      value: 'diary',
      label: t('photosPage.diaryEntry'),
      startDecorator: <SVGAssets.DiaryIcon className={iconClass} />
    }
  ];

  const filterConfigs: FilterConfig[] = [
    {
      key: 'mediaType',
      placeholder: t('photosPage.mediaType'),
      options: [
        { value: 'image', label: t('photosPage.images') },
        { value: 'video', label: t('photosPage.videos') },
        { value: 'document', label: t('photosPage.documents') }
      ]
    },
    {
      key: 'things',
      placeholder: t('photosPage.things'),
      options: thingsOptions
    },
    {
      key: 'people',
      placeholder: t('photosPage.people'),
      options: peopleOptions
    },
    {
      key: 'locations',
      placeholder: t('photosPage.location'),
      options: locationOptions
    },
    {
      key: 'sharedWith',
      placeholder: t('photosPage.sharedWith'),
      options: [{ value: '', label: '' }]
    }
  ];

  const recentOptions = [
    { value: 'mostRecent', label: t('photosPage.mostRecent') },
    { value: 'leastRecent', label: t('photosPage.leastRecent') }
  ];

  return (
    <div className="flex flex-wrap gap-1 items-center">
      <Button
        btnStyle="link"
        onClick={handleFavoritesToggle}
        className={`flex items-center py-1.5 rounded-md transition-colors`}>
        {isFavoritesActive ? (
          <SVGAssets.YellowFav className="w-5 h-5 mr-2" />
        ) : (
          <SVGAssets.FavoritesIcon className={`w-5 h-5 mr-2 `} />
        )}
        <span className={`text-sm font-semibold text-[#505050] cursor-pointer`}>
          {t('favorites')}
        </span>
      </Button>

      {/* ✅ MEDIA TYPE */}
      <MultiSelectDropdown
        key="mediaType"
        name="mediaType"
        options={filterConfigs[0].options}
        placeholder={filterConfigs[0].placeholder}
        value={filterOptions?.mediaType || []}
        onChange={selectedValues =>
          handleFilterChange('mediaType', selectedValues)
        }
        searchable
        searchPlaceholder="Search"
      />

      {/* ✅ DATE RANGE */}
      <DateRangePicker
        placeholder={t('photosPage.date')}
        value={filterOptions?.date || { months: [], years: [] }}
        onChange={selectedValues => handleFilterChange('date', selectedValues)}
      />

      {/* ✅ OTHER FILTERS */}
      {filterConfigs.slice(1).map(filter => (
        <MultiSelectDropdown
          key={filter.key}
          name={filter.key}
          options={filter.options}
          placeholder={filter.placeholder}
          value={filterOptions?.[filter.key] || []}
          onChange={selectedValues =>
            handleFilterChange(filter.key, selectedValues)
          }
          searchable
          searchPlaceholder="Search"
          onlyAll={filter.key === 'sharedWith'}
        />
      ))}

      {/* ✅ INTERACTIONS */}
      <MultiSelectDropdown
        name="interactions"
        options={interactionOptions}
        placeholder={t('photosPage.interactions')}
        value={filterOptions?.interactions || []}
        onChange={selectedValues =>
          handleFilterChange('interactions', selectedValues)
        }
        searchable
      />

      {/* View Mode Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <Button
          btnStyle="link"
          onClick={() => setIsViewDropdownOpen(!isViewDropdownOpen)}
          className="px-1 py-1.5 text-sm rounded-md bg-white hover:bg-gray-50 transition-colors flex items-center gap-2">
          {selectedView === 'grid' ? (
            <SVGAssets.GridIcon className="w-4 h-4" />
          ) : (
            <SVGAssets.ListIcon className="w-4 h-4" />
          )}
          {/* <span className="capitalize">{selectedView}</span> */}
          <SVGAssets.PricingArrow
            className={`w-3 h-3 transition-transform ${
              isViewDropdownOpen ? 'rotate-90' : '-rotate-90'
            }`}
          />
        </Button>

        {isViewDropdownOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50">
            <Button
              btnStyle="link"
              onClick={() => handleViewSelect('grid')}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                selectedView === 'grid' ? 'bg-gray-100 font-medium' : ''
              }`}>
              <SVGAssets.GridIcon className="w-4 h-4" />
              <span>{t('gridView')}</span>
            </Button>
            <Button
              btnStyle="link"
              onClick={() => handleViewSelect('list')}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                selectedView === 'list' ? 'bg-gray-100 font-medium' : ''
              }`}>
              <SVGAssets.ListIcon className="w-4 h-4" />
              <span>{t('listView')}</span>
            </Button>
          </div>
        )}
      </div>

      <Select
        name="sortBy"
        options={recentOptions}
        value={filterOptions?.sortBy || 'mostRecent'}
        onChange={e => handleFilterChange('sortBy', e.target.value)}
        labelClassName="!text-[#49712B] !font-semibold"
        selectClassName="text-sm"
        optionClassName="text-sm !text-[#49712B] !font-semibold"
      />
    </div>
  );
};
